import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { HouseholdActionsMenu } from "@/components/layout/Common/HouseholdActionsMenu"
import { HouseholdCard } from "@/components/molecules/HouseholdCard"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddHousehold } from "@/features/households"
import { usePermissions } from "@/hooks/usePermissions"
import { useIsMobile } from "@/hooks/useResponsive"
import { api } from "@/services/api"
import type {
  HouseholdListResponse,
  HouseholdResponse,
} from "@/types/household"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const householdsSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getHouseholdsQueryOptions({
  page,
  search,
  sort,
  direction,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
}) {
  const debugHouseholds =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_HOUSEHOLDS === "true"

  return {
    queryFn: async () => {
      if (debugHouseholds) {
        console.log(
          `Households page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response = await api.households.readHouseholdsApiV1HouseholdsGet({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search,
        sortBy: sort,
        sortOrder: direction,
      })

      const payload =
        unwrapStandardResponse<HouseholdListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugHouseholds) {
        console.log("Households page: API payload:", payload)
        console.log("🔍 Households page: items count:", items.length)
        console.log("🔍 Households page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "households",
      {
        page,
        search,
        sort,
        direction,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/households")({
  component: Households,
  validateSearch: (search) => householdsSearchSchema.parse(search),
})

function Households() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page, search, sort, direction } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  const [viewMode, setViewMode] = useState<ViewMode>("table")
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getHouseholdsQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("households:update") || hasPermission("households:delete")

  const columns = useMemo<DataTableColumn<HouseholdResponse>[]>(() => {
    const tableColumns: DataTableColumn<HouseholdResponse>[] = []

    // User-friendly columns showing essential household information
    tableColumns.push({
      key: "parent_name",
      label: "Parent/Guardian",
      render: (household) => (
        <Text fontWeight="medium">
          {household.parent_first_name} {household.parent_last_name}
        </Text>
      ),
    })
    tableColumns.push({
      key: "user_email",
      label: "Account Email",
      render: (household) => (
        <Text>{household.user_email || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "email",
      label: "Contact Email",
      render: (household) => (
        <Text>{formatFieldValue(household.email, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "phone",
      label: "Phone",
      render: (household) => (
        <Text>{formatFieldValue(household.phone, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "address",
      label: "Address",
      render: (household) => (
        <Text truncate maxW="200px">
          {formatFieldValue(household.address, "str")}
        </Text>
      ),
    })

    tableColumns.push({
      key: "emergency_contact",
      label: "Emergency Contact",
      render: (household) => (
        <Box>
          <Text fontSize="sm" fontWeight="medium">
            {formatFieldValue(household.emergency_contact_name, "str")}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {formatFieldValue(household.emergency_contact_phone, "str")}
          </Text>
        </Box>
      ),
    })

    tableColumns.push({
      key: "is_active",
      label: "Status",
      render: (household) => {
        const isActive = household.is_active !== false
        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={isActive ? "green.50" : "gray.50"}
            color={isActive ? "green.700" : "gray.700"}
          >
            {isActive ? "Active" : "Inactive"}
          </Box>
        )
      },
    })

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (household) => <HouseholdActionsMenu household={household} />,
      })
    }

    return tableColumns
  }, [showActions])

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Families</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading families...</Text>
        </Flex>
      </Container>
    )
  }

  return (
    <Container maxW="full">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 3, md: 0 }}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg">Families</Heading>
          <Box display={{ base: "block", md: "none" }}>
            <AddHousehold />
          </Box>
        </Flex>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search families"
                size="sm"
                width={{ base: "full", md: "220px" }}
                aria-label="Search families"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <HStack gap={2}>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={sort}
              onChange={(event) =>
                handleSortChange(
                  (event.target as HTMLSelectElement).value as SortField,
                )
              }
              aria-label="Sort families"
              width={{ base: "full", md: "170px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="created_at">Newest</option>
              <option value="updated_at">Recently updated</option>
              <option value="name">Name</option>
            </Box>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={direction}
              onChange={(event) =>
                handleDirectionChange(
                  (event.target as HTMLSelectElement).value as SortDirection,
                )
              }
              aria-label="Sort direction"
              width={{ base: "full", md: "150px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Box>
          </HStack>
          <Box display={{ base: "none", md: "flex" }}>
            <HStack gap={4}>
              <ViewToggle value={viewMode} onChange={setViewMode} />
              <AddHousehold />
            </HStack>
          </Box>
        </Flex>
      </Flex>

      {records.length === 0 ? (
        <VStack gap={4} py={12} alignItems="center">
          <FiSearch size={48} color="gray" />
          <VStack textAlign="center" gap={2}>
            <Heading size="md" color="gray.600">
              You don't have any families yet
            </Heading>
            <Text color="gray">Add a new family to get started</Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            {effectiveViewMode === "cards" ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {records.map((household: HouseholdResponse) => (
                  <HouseholdCard
                    key={household.id}
                    household={household}
                    showActions={showActions}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <DataTable columns={columns} data={records} size="sm" />
            )}
          </Box>
          <Flex
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Container>
  )
}
